import { Alert, Divider, List, ListItem, ListItemText, Typography } from '@mui/material';
import { Stack } from '@mui/system';

export function ProposalHelpContent() {
    return (
        <Stack spacing={2.5}>
            <Typography variant="h6">Como funcionam as propostas</Typography>

            <Typography variant="body2" color="text.secondary">
                Uma proposta é o processo comercial de adesão ou atualização de um clube no Eurobem. Ela reúne os dados do contratante, o plano,
                customizações, benefícios adicionais, texto do contrato, assinatura digital e pagamento.
            </Typography>

            <Divider />

            <Typography variant="subtitle1" fontWeight="bold">
                Etapas de uma proposta nova (sem clube)
            </Typography>

            <List dense disablePadding sx={{ listStyleType: 'decimal', pl: 2 }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                    <ListItemText
                        primary="Criar a proposta"
                        secondary="Informe o plano e os dados mínimos do contratante."
                    />
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                    <ListItemText
                        primary="Completar os blocos"
                        secondary="Termo, mensalidade, fidelidade, customização, benefícios adicionais e texto do contrato. Cada bloco é salvo separadamente."
                    />
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                    <ListItemText
                        primary="Gerar e assinar o contrato"
                        secondary="O documento é enviado por e-mail para assinatura digital (ZapSign). Envie o link de assinatura ao contratante, se necessário."
                    />
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                    <ListItemText
                        primary="Confirmar o pagamento"
                        secondary="Após a assinatura e o pagamento da mensalidade, o clube pode ser criado no sistema."
                    />
                </ListItem>
            </List>

            <Alert severity="info" variant="outlined">
                Em propostas para clube novo, a criação do clube ocorre depois que o contrato está concluído e os pagamentos confirmados.
            </Alert>

            <Divider />

            <Typography variant="subtitle1" fontWeight="bold">
                Atualizar um clube existente (com clube na criação)
            </Typography>

            <Typography variant="body2" color="text.secondary">
                Ao criar uma proposta, você pode selecionar um clube já cadastrado. Use essa opção quando o objetivo for{' '}
                <strong>trocar o plano ou renovar o contrato</strong> de um clube que já opera no Eurobem — não para cadastrar um clube do zero.
            </Typography>

            <Typography variant="body2" color="text.secondary">
                Ao selecionar o clube, os dados do contratante são pré-preenchidos automaticamente. O sistema também impede{' '}
                <strong>downgrade de plano</strong> (trocar para um plano mais barato que o atual).
            </Typography>

            <Typography variant="subtitle2" fontWeight="bold" sx={{ pt: 0.5 }}>
                O que acontece na prática
            </Typography>

            <List dense disablePadding>
                <ListItem sx={{ py: 0.25 }}>
                    <ListItemText
                        primary="Durante a proposta"
                        secondary="As alterações ficam no novo contrato em elaboração. O clube continua operando com o contrato anterior até a assinatura."
                    />
                </ListItem>
                <ListItem sx={{ py: 0.25 }}>
                    <ListItemText
                        primary="Na assinatura do contrato"
                        secondary="Quando o contratante assina no ZapSign, o sistema aplica automaticamente o novo plano no clube."
                    />
                </ListItem>
                <ListItem sx={{ py: 0.25 }}>
                    <ListItemText
                        primary="Assinatura de pagamento (Asaas)"
                        secondary="A cobrança recorrente existente é mantida; apenas o valor e os dados do plano são atualizados."
                    />
                </ListItem>
                <ListItem sx={{ py: 0.25 }}>
                    <ListItemText
                        primary="Contrato anterior"
                        secondary="É marcado como atualizado e deixa de ser o contrato vigente do clube."
                    />
                </ListItem>
            </List>

            <Typography variant="subtitle2" fontWeight="bold" sx={{ pt: 0.5 }}>
                O que é aplicado no clube após a assinatura
            </Typography>

            <List dense disablePadding>
                <ListItem sx={{ py: 0.25 }}>
                    <ListItemText primary="Funções do plano (ex.: app, rede de descontos, cashback)" />
                </ListItem>
                <ListItem sx={{ py: 0.25 }}>
                    <ListItemText primary="Tipos de benefícios adicionais habilitados no plano" />
                </ListItem>
                <ListItem sx={{ py: 0.25 }}>
                    <ListItemText primary="Customizações contratadas (app personalizado, telemedicina customizada, etc.)" />
                </ListItem>
                <ListItem sx={{ py: 0.25 }}>
                    <ListItemText primary="Produtos de revenda e preços acordados na proposta" />
                </ListItem>
                <ListItem sx={{ py: 0.25 }}>
                    <ListItemText primary="Vínculo com o novo contrato vigente" />
                </ListItem>
            </List>

            <Alert severity="warning" variant="outlined">
                Nome fantasia, CNPJ, endereço e usuário dono do clube <strong>não são alterados</strong> nesse fluxo de atualização — apenas as
                condições comerciais e técnicas do plano.
            </Alert>

            <Divider />

            <Typography variant="subtitle1" fontWeight="bold">
                Link de assinatura
            </Typography>

            <Typography variant="body2" color="text.secondary">
                Na página de detalhe da proposta há um link que pode ser enviado ao contratante para que ele conclua a adesão (dados, pagamento e
                assinatura) pelo fluxo público.
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold">
                Dicas rápidas
            </Typography>

            <List dense disablePadding>
                <ListItem sx={{ py: 0.25 }}>
                    <ListItemText primary="Salve cada bloco antes de avançar para o próximo." />
                </ListItem>
                <ListItem sx={{ py: 0.25 }}>
                    <ListItemText primary="Use a aba «Contrato Gerado» para ver propostas que já têm documento no ZapSign." />
                </ListItem>
                <ListItem sx={{ py: 0.25 }}>
                    <ListItemText primary="A aba «Canceladas» lista propostas canceladas. O super pode excluir permanentemente qualquer proposta, inclusive assinada ou paga." />
                </ListItem>
                <ListItem sx={{ py: 0.25 }}>
                    <ListItemText primary="Propostas vinculadas a clube aparecem com o nome do clube na listagem." />
                </ListItem>
            </List>
        </Stack>
    );
}
